/**
 * Dev Config Service
 * Manages runtime configuration for development environment
 * This file should only be imported in development builds
 */

export interface DevConfig {
  // API Provider Selection
  useRealApi: boolean;
  
  // Mock API Behavior Flags (only apply when useRealApi is false)
  mockApi: {
    mockLogin: boolean;
    returnEmpty: boolean;
    isAuthenticated: boolean;
    failCancellation: boolean;
    failUpdate: boolean;
    failActivation: boolean;
    failSetupIntentCreation: boolean;
  };
}

const DEFAULT_CONFIG: DevConfig = {
  useRealApi: false,
  mockApi: {
    mockLogin: true,
    returnEmpty: false,
    isAuthenticated: true,
    failCancellation: false,
    failUpdate: false,
    failActivation: false,
    failSetupIntentCreation: false,
  },
};

const STORAGE_KEY = 'nexar-dev-config';

class DevConfigService {
  private config: DevConfig;
  private listeners: Set<(config: DevConfig) => void> = new Set();

  constructor() {
    this.config = this.loadConfig();
  }

  private loadConfig(): DevConfig {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return {
          ...DEFAULT_CONFIG,
          ...parsed,
          mockApi: {
            ...DEFAULT_CONFIG.mockApi,
            ...(parsed.mockApi || {}),
          },
        };
      }
    } catch (error) {
      console.warn('Failed to load dev config from localStorage:', error);
    }
    return { ...DEFAULT_CONFIG };
  }

  private saveConfig(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.warn('Failed to save dev config to localStorage:', error);
    }
  }

  getConfig(): DevConfig {
    return { ...this.config };
  }

  updateConfig(updates: Partial<DevConfig>): void {
    const { mockApi, ...restUpdates } = updates;
    this.config = {
      ...this.config,
      ...restUpdates,
      mockApi: {
        ...this.config.mockApi,
        ...(mockApi || {}),
      },
    };
    this.saveConfig();
    this.notifyListeners();
  }

  resetConfig(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.saveConfig();
    this.notifyListeners();
  }

  subscribe(listener: (config: DevConfig) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.config));
  }
}

export const devConfig = new DevConfigService();



